import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StatusBadge, type StatusVariant } from '@/shared/components/status-badge';
import { cn } from '@/shared/lib/utils';

/** Plain "contains" on the label: predictable for names and amounts (no fuzzy matches). */
function containsLabel(_value: string, search: string, keywords?: string[]): number {
  const needle = search.trim().toLowerCase();
  if (!needle) return 1;
  return (keywords ?? []).some((keyword) => keyword.toLowerCase().includes(needle)) ? 1 : 0;
}

export interface SearchableOption {
  value: string;
  label: string;
  /** Muted secondary text beside the label (e.g. a code); also matched by the search. */
  description?: string;
  /** A short status shown beside the option (and on the trigger when selected), e.g. a warning. */
  hint?: { text: string; variant: StatusVariant };
  /** Listed but not selectable — say why with a `hint`. */
  disabled?: boolean;
}

/**
 * One field that is both the search box and the select: type to narrow the
 * list, pick an option. With `onSearchChange` the search runs on the server
 * (the caller passes the matching options); without it the options are
 * filtered locally.
 */
export function SearchableSelect({
  id,
  label,
  value,
  selectedLabel,
  options,
  onChange,
  placeholder,
  searchPlaceholder = 'Search…',
  emptyText = 'Nothing found.',
  search,
  onSearchChange,
  loading = false,
  disabled = false,
  triggerClassName,
}: {
  id: string;
  /** Accessible name of the trigger (the visible label is rendered by the caller). */
  label: string;
  value: string | null;
  /** Shown on the trigger when the selected value is not among the current options. */
  selectedLabel?: string | null;
  options: SearchableOption[];
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder?: string;
  emptyText?: string;
  search?: string;
  onSearchChange?: (search: string) => void;
  loading?: boolean;
  disabled?: boolean;
  /** Overrides the trigger's default compact sizing (e.g. `h-9 text-sm` in full-size forms). */
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const serverSearch = onSearchChange !== undefined;
  const selected = options.find((option) => option.value === value);
  const current = selected?.label ?? selectedLabel ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'border-input text-foreground hover:border-input hover:text-foreground h-8 w-full justify-between px-3 text-xs font-normal',
            triggerClassName,
          )}
        >
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-start',
              !current && 'text-muted-foreground',
            )}
          >
            {current ?? placeholder}
          </span>
          {selected?.description && (
            <span className="text-muted-foreground shrink-0 font-mono text-[11px]">
              {selected.description}
            </span>
          )}
          {selected?.hint && (
            <StatusBadge variant={selected.hint.variant} className="ms-auto">
              {selected.hint.text}
            </StatusBadge>
          )}
          <ChevronDown className="text-muted-foreground size-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command shouldFilter={!serverSearch} filter={containsLabel}>
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-8 text-xs"
            {...(serverSearch ? { value: search, onValueChange: onSearchChange } : {})}
          />
          <CommandList>
            {loading ? (
              <div className="text-muted-foreground flex items-center gap-2 p-3 text-xs">
                <Loader2 className="size-3.5 animate-spin" aria-hidden /> Searching…
              </div>
            ) : (
              <CommandEmpty className="py-3 text-center text-xs">{emptyText}</CommandEmpty>
            )}
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                keywords={option.description ? [option.label, option.description] : [option.label]}
                disabled={option.disabled}
                onSelect={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className="text-xs"
              >
                <Check
                  className={cn('size-3.5', option.value === value ? 'opacity-100' : 'opacity-0')}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.description && (
                  <span className="text-muted-foreground shrink-0 font-mono text-[11px]">
                    {option.description}
                  </span>
                )}
                {option.hint && (
                  <StatusBadge variant={option.hint.variant}>{option.hint.text}</StatusBadge>
                )}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
